// Setup → Groups and routing (api-phase2.md "Groups and skills"; prototype setup 'routing'):
//   groups: name, kind, overflow group, overflow after (s), accept timeout (s), active, members with a
//           primary / secondary radio (a user's primary group is where their raised events go)
//   skills: the skill list, with add
//   offer rules: the routing mode and cap from /config.json (read-only here)
// Admins edit; supervisors read.

import { useState } from 'react';
import { api, GROUP_KINDS, type Group, type GroupKind, type GroupPatch, type SetupUser, type Skill } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { Card, Empty, ErrorBox, InlineError, Loading } from '../../components/ui.tsx';
import { useLoad } from '../../hooks.ts';
import { useSession } from '../../session.tsx';
import { SaveBar } from './common.tsx';

interface Draft {
  name: string;
  kind: GroupKind;
  overflowGroupId: string | null;
  overflowAfterS: number;
  acceptTimeoutS: number;
  active: boolean;
  members: { userId: string; displayName: string; primary: boolean }[];
}

const draftOf = (g: Group): Draft => ({
  name: g.name,
  kind: g.kind,
  overflowGroupId: g.overflowGroupId,
  overflowAfterS: g.overflowAfterS,
  acceptTimeoutS: g.acceptTimeoutS,
  active: g.active,
  members: g.members.map((m) => ({ ...m })),
});

function patchOf(g: Group, d: Draft): GroupPatch {
  const p: GroupPatch = {};
  if (d.name.trim() !== g.name) p.name = d.name.trim();
  if (d.kind !== g.kind) p.kind = d.kind;
  if (d.overflowGroupId !== g.overflowGroupId) p.overflowGroupId = d.overflowGroupId;
  if (d.overflowAfterS !== g.overflowAfterS) p.overflowAfterS = d.overflowAfterS;
  if (d.acceptTimeoutS !== g.acceptTimeoutS) p.acceptTimeoutS = d.acceptTimeoutS;
  if (d.active !== g.active) p.active = d.active;
  const key = (l: { userId: string; primary: boolean }[]) =>
    l
      .map((m) => `${m.userId}:${m.primary ? 1 : 0}`)
      .sort()
      .join();
  if (key(d.members) !== key(g.members)) p.members = d.members.map((m) => ({ userId: m.userId, primary: m.primary }));
  return p;
}

function GroupForm({ g, groups, users, canEdit, onSaved }: { g: Group; groups: Group[]; users: SetupUser[] | undefined; canEdit: boolean; onSaved: (g: Group) => void }) {
  const [d, setD] = useState<Draft>(() => draftOf(g));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [adding, setAdding] = useState('');
  const patch = patchOf(g, d);
  const dirty = Object.keys(patch).length > 0;
  const bad = !d.name.trim() || d.overflowAfterS < 0 || d.acceptTimeoutS < 5 || d.overflowGroupId === g.id;
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    setSaved(false);
    setD((x) => ({ ...x, [k]: v }));
  };
  const save = async () => {
    if (bad) return;
    setBusy(true);
    setErr(null);
    try {
      const n = await api.patchGroup(g.id, patch);
      onSaved(n);
      setD(draftOf(n));
      setSaved(true);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  const nonMembers = (users ?? []).filter((u) => u.status === 'active' && !d.members.some((m) => m.userId === u.id)).sort((a, b) => a.displayName.localeCompare(b.displayName));

  return (
    <fieldset disabled={!canEdit || busy} className="tabbody">
      <div className="fgrid">
        <label className="field">
          <span className="lbl">Name</span>
          <input value={d.name} onChange={(e) => set('name', e.target.value)} aria-invalid={!d.name.trim()} />
        </label>
        <label className="field">
          <span className="lbl">Kind</span>
          <select value={d.kind} onChange={(e) => set('kind', e.target.value as GroupKind)}>
            {GROUP_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="lbl">Overflow to</span>
          <select value={d.overflowGroupId ?? ''} onChange={(e) => set('overflowGroupId', e.target.value || null)}>
            <option value="">No overflow</option>
            {groups
              .filter((x) => x.id !== g.id)
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                  {x.active ? '' : ' (inactive)'}
                </option>
              ))}
          </select>
        </label>
        <label className="field">
          <span className="lbl">Overflow after (s)</span>
          <input type="number" min={0} value={d.overflowAfterS} onChange={(e) => set('overflowAfterS', Math.max(0, Math.round(Number(e.target.value) || 0)))} disabled={!d.overflowGroupId} />
          <span className="note">Nobody eligible in this group for this long: the event moves on.</span>
        </label>
        <label className="field">
          <span className="lbl">Accept timeout (s)</span>
          <input type="number" min={5} value={d.acceptTimeoutS} onChange={(e) => set('acceptTimeoutS', Math.round(Number(e.target.value) || 0))} aria-invalid={d.acceptTimeoutS < 5} />
          <span className="note">How long an offer waits for Accept.</span>
        </label>
        <label className="field end">
          <span className="ck-inline">
            <input type="checkbox" checked={d.active} onChange={(e) => set('active', e.target.checked)} />
            Active
          </span>
        </label>
      </div>

      <div className="sec">
        <div className="lbl">
          <span>Members · {d.members.length}</span>
        </div>
        {d.members.length === 0 && <div className="note">No members yet.</div>}
        {d.members.length > 0 && (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th scope="col">Member</th>
                  <th scope="col">This group is their</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {d.members.map((m) => (
                  <tr key={m.userId}>
                    <td>{m.displayName}</td>
                    <td>
                      <div className="seg" role="radiogroup" aria-label={`${m.displayName}: primary or secondary group`}>
                        {([true, false] as const).map((p) => (
                          <button
                            key={String(p)}
                            type="button"
                            role="radio"
                            aria-checked={m.primary === p}
                            className={m.primary === p ? 'on' : ''}
                            onClick={() => set('members', d.members.map((x) => (x.userId === m.userId ? { ...x, primary: p } : x)))}
                          >
                            {p ? 'Primary' : 'Secondary'}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td>
                      {canEdit && (
                        <button type="button" className="btn icon sm" onClick={() => set('members', d.members.filter((x) => x.userId !== m.userId))} aria-label={`Remove ${m.displayName}`}>
                          <Icon name="trash" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {canEdit && (
          <div className="row-wrap">
            <select className="compact" value={adding} onChange={(e) => setAdding(e.target.value)} aria-label="Add member" disabled={!users}>
              <option value="">{users ? 'Add a member…' : 'Loading users…'}</option>
              {nonMembers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName} · {u.roles.join(', ') || 'no roles'}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn sm"
              disabled={!adding}
              onClick={() => {
                const u = users?.find((x) => x.id === adding);
                if (!u) return;
                const hasPrimary = (u.groups ?? []).some((x) => x.primary && x.id !== g.id);
                set('members', [...d.members, { userId: u.id, displayName: u.displayName, primary: !hasPrimary }]);
                setAdding('');
              }}
            >
              <Icon name="plus" />
              Add
            </button>
          </div>
        )}
        <div className="note">A person has one primary group: raised events they cannot take go there first. Making this group primary for someone replaces their other primary.</div>
      </div>
      {d.overflowGroupId === g.id && <span className="inline-err">A group cannot overflow to itself.</span>}
      <SaveBar canEdit={canEdit} dirty={dirty} busy={busy} error={err} saved={saved} onSave={() => void save()} onReset={() => setD(draftOf(g))} />
    </fieldset>
  );
}

function NewGroup({ groups, onCreated }: { groups: Group[]; onCreated: (g: Group) => void }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<GroupKind>('queue');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const dup = groups.some((g) => g.name.toLowerCase() === name.trim().toLowerCase());
  const create = async () => {
    if (!name.trim() || dup) return;
    setBusy(true);
    setErr(null);
    try {
      onCreated(await api.createGroup({ name: name.trim(), kind }));
      setName('');
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="editrow">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New group (e.g. Tier 1 · Night)" aria-label="New group name" onKeyDown={(e) => e.key === 'Enter' && void create()} />
      <select className="compact" value={kind} onChange={(e) => setKind(e.target.value as GroupKind)} aria-label="Kind">
        {GROUP_KINDS.map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      </select>
      <button type="button" className="btn sm pri" onClick={() => void create()} disabled={busy || !name.trim() || dup}>
        <Icon name="plus" />
        Create
      </button>
      {dup && <span className="note warn-t">That name exists.</span>}
      <InlineError error={err} />
    </div>
  );
}

function SkillsCard({ canEdit }: { canEdit: boolean }) {
  const load = useLoad((sig) => api.skills(sig), []);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const codeOk = /^[a-z0-9][a-z0-9_:.-]{0,47}$/.test(code.trim());
  const dup = (load.data ?? []).some((s) => s.code === code.trim());
  const add = async () => {
    if (!codeOk || dup || !label.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const sk = await api.createSkill({ code: code.trim(), label: label.trim() });
      load.setData((l) => [...(l ?? []), sk]);
      setCode('');
      setLabel('');
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card
      title={
        <>
          <Icon name="tag" />
          Skills
        </>
      }
    >
      <div className="note">An operator is offered a site's events only if they have every skill the site requires (Setup → Sites → Operations). Give people skills in Users.</div>
      {load.error && !load.data ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load skills" />
      ) : !load.data ? (
        <Loading label="Loading skills…" />
      ) : load.data.length === 0 ? (
        <Empty>No skills yet.</Empty>
      ) : (
        <div className="chips">
          {load.data.map((s: Skill) => (
            <span key={s.code} className="chip" title={s.code}>
              {s.label} <span className="mono dim">{s.code}</span>
            </span>
          ))}
        </div>
      )}
      {canEdit && (
        <div className="editrow">
          <input className="mono" value={code} onChange={(e) => setCode(e.target.value.toLowerCase())} placeholder="code (e.g. lang:es)" aria-label="Skill code" aria-invalid={!!code && (!codeOk || dup)} />
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. Spanish talk-down)" aria-label="Skill label" onKeyDown={(e) => e.key === 'Enter' && void add()} />
          <button type="button" className="btn sm pri" onClick={() => void add()} disabled={busy || !codeOk || dup || !label.trim()}>
            <Icon name="plus" />
            Add skill
          </button>
          {code && !codeOk && <span className="note warn-t">Lowercase letters, digits and : . - _</span>}
          {dup && <span className="note warn-t">That code exists.</span>}
          <InlineError error={err} />
        </div>
      )}
    </Card>
  );
}

export function GroupsEditor({ canEdit }: { canEdit: boolean }) {
  const s = useSession();
  const load = useLoad((sig) => api.groups(sig), []);
  const users = useLoad((sig) => (canEdit ? api.setupUsers(sig) : Promise.resolve<SetupUser[]>([])), [canEdit]);
  const [sel, setSel] = useState<string | null>(null);
  const groups = (load.data ?? []).slice().sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
  const cur = groups.find((g) => g.id === sel) ?? groups[0] ?? null;
  const name = (id: string | null) => groups.find((g) => g.id === id)?.name ?? '—';
  const r = s.config.routing;

  return (
    <>
      <Card
        title={
          <>
            <Icon name="route" />
            Groups and routing
          </>
        }
      >
        {load.error && !load.data ? (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load groups" />
        ) : !load.data ? (
          <Loading label="Loading groups…" />
        ) : (
          <>
            {groups.length === 0 ? (
              <Empty>No groups yet. Create the first one below.</Empty>
            ) : (
              <div className="tw">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Group</th>
                      <th scope="col">Kind</th>
                      <th scope="col" className="num">
                        Members
                      </th>
                      <th scope="col">Overflow to</th>
                      <th scope="col" className="num">
                        Accept timeout
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <tr
                        key={g.id}
                        className={`click${cur?.id === g.id ? ' sel' : ''}${g.active ? '' : ' inactive'}`}
                        onClick={() => setSel(g.id)}
                        onKeyDown={(e) => e.key === 'Enter' && setSel(g.id)}
                        tabIndex={0}
                        aria-selected={cur?.id === g.id}
                      >
                        <td className="strong">{g.name}</td>
                        <td>{g.kind}</td>
                        <td className="mono num">{g.members.length}</td>
                        <td>{g.overflowGroupId ? `${name(g.overflowGroupId)} after ${g.overflowAfterS} s` : <span className="dim">—</span>}</td>
                        <td className="mono num">{g.acceptTimeoutS} s</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {canEdit && <NewGroup groups={groups} onCreated={(g) => (load.setData((l) => [...(l ?? []), g]), setSel(g.id))} />}
          </>
        )}
      </Card>
      {cur && (
        <Card
          title={
            <>
              <Icon name="users" />
              {cur.name}
            </>
          }
        >
          {users.error && canEdit ? <InlineError error={users.error} /> : null}
          <GroupForm
            key={cur.id}
            g={cur}
            groups={groups}
            users={canEdit ? users.data : []}
            canEdit={canEdit}
            onSaved={(n) => {
              load.setData((l) => (l ?? []).map((x) => (x.id === n.id ? n : x)));
              users.reload();
            }}
          />
        </Card>
      )}
      <SkillsCard canEdit={canEdit} />
      <Card
        title={
          <>
            <Icon name="gear" />
            Offer rules
          </>
        }
      >
        <div className="fgrid">
          <div className="ro">
            <span className="lbl">Routing</span>
            <span>{r.mode === 'push' ? 'Push: the next event is offered to the best available operator' : 'Pull: operators claim from the queue'}</span>
          </div>
          <div className="ro">
            <span className="lbl">Operators may claim</span>
            <span>{r.allowPull ? 'Yes' : 'No (offers only)'}</span>
          </div>
          <div className="ro">
            <span className="lbl">Active events per operator</span>
            <span className="mono">{s.config.maxActiveEvents}</span>
          </div>
        </div>
        <div className="note">These come from the server configuration (/config.json). Offers go to the operator idle longest, then with the fewest active events.</div>
      </Card>
    </>
  );
}
